import {
	AlertTriangle,
	LayoutDashboard,
	LogOut,
	MessageSquare,
	Settings,
	StickyNote,
	Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { signOut } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

const navItems = [
	{ href: "/", label: "Dashboard", icon: LayoutDashboard },
	{ href: "/chats", label: "Chats", icon: MessageSquare },
	{ href: "/notes", label: "Notes", icon: StickyNote },
	{ href: "/alerts", label: "Alerts", icon: AlertTriangle },
	{ href: "/settings/team", label: "Team", icon: Users },
	{ href: "/settings", label: "Settings", icon: Settings },
];

export function Sidebar() {
	const [location] = useLocation();
	const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

	const handleSignOut = async () => {
		await signOut();
		window.location.href = "/login";
	};

	return (
		<TooltipProvider delayDuration={0}>
			<aside className="fixed left-0 top-0 z-40 h-screen w-16 lg:w-64 bg-sidebar border-r border-sidebar-border flex flex-col transition-all duration-300">
				{/* Logo */}
				<div className="h-20 flex items-center justify-center lg:justify-start px-6">
					<Link href="/">
						<div className="flex items-center gap-3 cursor-pointer group">
							<div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-200 p-1.5">
								<img
									src="/emantime-logo.png"
									alt="EmanTime"
									className="w-full h-full object-contain"
								/>
							</div>
							<div className="hidden lg:flex flex-col">
								<span className="text-white font-bold text-lg leading-tight">
									Eman Time
								</span>
								<span className="text-sidebar-foreground/60 text-[10px] font-medium uppercase tracking-wider">
									Dashboard
								</span>
							</div>
						</div>
					</Link>
				</div>

				{/* Navigation */}
				<nav className="flex-1 py-4 px-2 lg:px-3 space-y-1">
					{navItems.map((item) => {
						const isActive =
							location === item.href ||
							(item.href !== "/" && location.startsWith(item.href));

						return (
							<Tooltip key={item.href}>
								<TooltipTrigger asChild>
									<Link href={item.href}>
										<div
											className={cn(
												"flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer",
												isActive
													? "bg-sidebar-accent text-sidebar-accent-foreground"
													: "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
											)}
										>
											<item.icon className="w-5 h-5 shrink-0" />
											<span className="hidden lg:block font-medium">
												{item.label}
											</span>
										</div>
									</Link>
								</TooltipTrigger>
								<TooltipContent side="right" className="lg:hidden">
									{item.label}
								</TooltipContent>
							</Tooltip>
						);
					})}
				</nav>

				{/* Sign Out */}
				<div className="p-2 lg:p-3 border-t border-sidebar-border">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								className="w-full justify-center lg:justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
								onClick={() => setShowLogoutConfirm(true)}
							>
								<LogOut className="w-5 h-5" />
								<span className="hidden lg:block ml-3">Sign Out</span>
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right" className="lg:hidden">
							Sign Out
						</TooltipContent>
					</Tooltip>
				</div>
			</aside>

			<ConfirmDialog
				open={showLogoutConfirm}
				onOpenChange={setShowLogoutConfirm}
				title="Sign Out"
				description="Are you sure you want to sign out? You will need to log in again to access the dashboard."
				confirmText="Sign Out"
				onConfirm={handleSignOut}
				variant="destructive"
			/>
		</TooltipProvider>
	);
}
